import { useEffect } from "react";
import { motion } from "framer-motion";
import { PawPrint, Mail } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ComingSoonModal } from "@/components/ComingSoonModal";
import { NAV_ROOMS } from "@/data/doorways";
import { useState } from "react";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay: 0.1 + i * 0.12 },
  }),
};

export default function Contact() {
  const [navRoom, setNavRoom] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const subscribeNav = async (email) => {
    const { data } = await api.post("/subscribe", {
      email,
      interest: navRoom.name,
      source: navRoom.source,
    });
    return data;
  };

  return (
    <div className="min-h-screen overflow-x-clip" data-testid="contact-page">
      <Header onOpenNavRoom={(key) => setNavRoom(NAV_ROOMS[key])} />

      <main className="mx-auto flex w-full max-w-2xl flex-col items-center px-6 pb-20 pt-4 text-center sm:pt-8">
        <motion.div variants={reveal} initial="hidden" animate="show" custom={0}>
          <img
            src="/images/brand-badge.jpg"
            alt="My Dachshund Days — two dachshunds among wildflowers"
            data-testid="contact-badge"
            className="h-44 w-44 rounded-full object-cover shadow-[0_20px_50px_-20px_rgba(42,37,30,0.35)] sm:h-56 sm:w-56"
          />
        </motion.div>

        <motion.div variants={reveal} initial="hidden" animate="show" custom={1} className="mt-8 flex items-center gap-3">
          <PawPrint className="h-4 w-4 text-[#C06A4E]" fill="#C06A4E" strokeWidth={0} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.32em] text-ink/55">
            We&rsquo;d love to hear from you
          </span>
          <PawPrint className="h-4 w-4 text-[#C06A4E]" fill="#C06A4E" strokeWidth={0} />
        </motion.div>

        <motion.h1
          variants={reveal}
          initial="hidden"
          animate="show"
          custom={2}
          data-testid="contact-title"
          className="mt-4 font-serif text-[40px] font-medium leading-tight tracking-tight text-ink sm:text-5xl"
        >
          Contact Us
        </motion.h1>

        <motion.div
          variants={reveal}
          initial="hidden"
          animate="show"
          custom={3}
          className="mt-7 space-y-5 text-[15px] leading-[1.9] text-ink/75 sm:text-base"
        >
          <p>We&rsquo;re so glad you stopped by.</p>
          <p>
            My Dachshund Days is a growing little world made for people who love life
            with dachshunds, and we&rsquo;re always happy to hear from fellow dachshund
            people.
          </p>
          <p>
            Whether you have a question, want to say hello, need help with something on
            the site, or simply want to share a dachshund story with us, we&rsquo;d love
            to hear from you.
          </p>
          <p>Please email us at:</p>
        </motion.div>

        <motion.div variants={reveal} initial="hidden" animate="show" custom={4} className="mt-6 flex flex-col items-center gap-4">
          <a
            href="mailto:hello@mydachshunddays.com"
            data-testid="contact-email-button"
            className="inline-flex min-h-[54px] items-center gap-2.5 whitespace-nowrap rounded-full bg-[#C06A4E] px-8 text-[12px] font-semibold uppercase tracking-[0.14em] text-ivory transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
          >
            <Mail className="h-4 w-4" />
            Email My Dachshund Days
          </a>
          <a
            href="mailto:hello@mydachshunddays.com"
            data-testid="contact-email-link"
            className="min-h-[44px] py-2 font-serif text-xl font-medium text-ink underline decoration-[#C06A4E]/50 decoration-1 underline-offset-8 transition-colors hover:text-[#C06A4E]"
          >
            hello@mydachshunddays.com
          </a>
        </motion.div>

        <motion.p
          variants={reveal}
          initial="hidden"
          animate="show"
          custom={5}
          className="mt-9 text-[15px] leading-[1.9] text-ink/75 sm:text-base"
        >
          We&rsquo;ll do our best to get back to you as soon as we can.
        </motion.p>

        <motion.p
          variants={reveal}
          initial="hidden"
          animate="show"
          custom={6}
          className="mt-10 font-serif text-lg italic text-ink/50"
        >
          Little dogs. Big inspiration.
        </motion.p>
      </main>

      <Footer />

      <ComingSoonModal
        room={navRoom}
        open={Boolean(navRoom)}
        onClose={() => setNavRoom(null)}
        onSubscribe={subscribeNav}
      />
    </div>
  );
}
